import { subHours, subDays } from 'date-fns';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    componentReaction,
    DateRangePicker,
    displayFormatTime,
    displayUtcToZonedTimeFactory,
    DropdownMultiSelectFilter,
    Enums,
    FormStore,
    IActiveElement,
    inject,
    LineGraph,
    Loader,
    minMax,
    RangeSlider, 
    SubTopBar,
    toKeyArray,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import * as Chart from 'chart.js';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import {
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Form,
    UncontrolledButtonDropdown,
    UncontrolledTooltip,
} from 'reactstrap';
import { AppStore } from '../../Core/AppStore';
import { DisplaysChartsStore } from './LiveCharts/DisplaysChartsStore';
import { DisplaysStore } from '../DisplaysStore';
import { BsListUl } from '@react-icons/all-files/bs/BsListUl';
import { BsLayoutSplit } from '@react-icons/all-files/bs/BsLayoutSplit';
import { BsBarChart } from '@react-icons/all-files/bs/BsBarChart';
import { BsArrowsAngleContract } from '@react-icons/all-files/bs/BsArrowsAngleContract';
import { BsArrowsAngleExpand } from '@react-icons/all-files/bs/BsArrowsAngleExpand';

interface IChartPoint {
    r?: number;
    x: number;
    y: number;
}

export interface ITelemetryChartsViewParams {
    serialNumber: string;
}

export interface ITelemetryChartsViewProps extends RouteComponentProps<ITelemetryChartsViewParams> {}

@autoinject
@observer
export class TelemetryChartsView extends React.Component<ITelemetryChartsViewProps> {
    public constructor(props: ITelemetryChartsViewProps) {
        super(props);
        const trackedValues = computed(() => [
            this.formStore.fields.startTime.value,
            this.formStore.fields.endTime.value,
            this.displaysChartsStore.storage.telemetryChartSelectedMeasurements,
        ]);
        
        let start = subHours(new Date(), 24);
        let end = new Date();
        this.formStore.fields.startTime.setValue(start);
        this.formStore.fields.endTime.setValue(end);
        
        componentReaction(this, () => trackedValues.get(), this.loadTrackData);
    }

    @action
    public async componentDidMount() {
        this.loading = true;
        // this.setInitialTimeRange();
        try {
            await this.displaysStore.loadAsync(this.props.match.params.serialNumber);
        } catch (e: any) {
            this.appStore.setError(e);
            return;
        }

        try {
            await this.displaysChartsStore.loadTelemetryInfoAsync(this.props.match.params.serialNumber);

            if (this.telemetryInfo) {
                this.loading = false;

                await this.loadTrackData();
            }
        } catch (e: any) {
            this.appStore.alertHandle.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        const renderBeforeFilter = (
            <>
                <button className="text-center dropdown-item" onClick={this.onFilterClear}>
                    Clear
                </button>
            </>
        );

        return (
            <div className="container-fluid p-3 h-100">
                <SubTopBar>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12 col-md-3">
                                <DropdownMultiSelectFilter
                                    filter={this.displaysChartsStore.storage.telemetryChartSelectedMeasurements.map(m => {
                                        return { option: m };
                                    })}
                                    options={this.options}
                                    onToggle={this.onFilterDropdownToggle}
                                    onChange={this.onSensorFilterChange}
                                    print={this.print}
                                    style={{
                                        minWidth: 400,
                                    }}
                                    className="d-inline-block ms-1"
                                    size="sm"
                                    title={<BsLayoutSplit />}
                                    defaultText={`Find Sensors...`}
                                    dynamicTitle={false}
                                    showActive={false}
                                    caret
                                    showClear={false}
                                    renderBefore={renderBeforeFilter}
                                />

                                <div className="mx-2 d-inline-block">
                                    <input
                                        type="radio"
                                        className="form-check-input"
                                        checked={this.forcePercentages}
                                        onClick={this.onForcePercentagesChange}
                                    />
                                    <Tooltip message="Forces values 0-1 to be 0-100">Force Percentages</Tooltip>
                                </div>

                                <div className="mx-2 d-inline-block">
                                    <input
                                        type="radio"
                                        className="form-check-input"
                                        checked={this.forceSingleYAxis}
                                        onClick={this.onForceSingleYAxis}
                                    />
                                    Single Y Axis
                                </div>
                                
                                <UncontrolledButtonDropdown className="ms-auto d-inline-block">
                                    <DropdownToggle caret size="lg" color="link">
                                        <BsBarChart />
                                    </DropdownToggle>

                                    <DropdownMenu end>
                                        <LinkContainer to={`/displays/${this.props.match.params.serialNumber}/telemetry/chart`}>
                                            <DropdownItem>
                                                <BsBarChart /> Performance Chart
                                            </DropdownItem>
                                        </LinkContainer>
                                        <LinkContainer to={`/displays/${this.props.match.params.serialNumber}/telemetry/table`}>
                                            <DropdownItem>
                                                <BsListUl className="" /> Performance List
                                            </DropdownItem>
                                        </LinkContainer>
                                    </DropdownMenu>
                                </UncontrolledButtonDropdown>
                            </div>

                            <div className="col-12 col-md-9">
                                <Form>
                                    <div className="d-block d-flex justify-content-center align-items-center w-100">
                                        <DateRangePicker
                                            startDate={this.formStore.fields.startTime}
                                            endDate={this.formStore.fields.endTime}
                                            className="d-inline-block me-2"
                                            timeFormat="h:mm a"
                                            timezone={this.timezone}
                                            showTimeSelect
                                            onChange={this.onDateChange}
                                        />
                                        <div className="d-none d-lg-inline-block">
                                            Latest:{' '}
                                            {this.display && this.display.pollTime
                                                ? displayFormatTime(
                                                    this.display.pollTime,
                                                    'EEEE, MMMM do yyyy, h:mm:ss a ZZZZ',
                                                    this.display.serialNumber
                                                )
                                                : 'N/A'}
                                        </div>
                                    </div>
                                </Form>
                            </div>
                        </div>
                        
                        <div className="col-12 text-center">
                            {this.telemetryInfo &&
                                this.formStore.fields.startTime.value &&
                                this.formStore.fields.endTime.value && (
                                    <RangeSlider
                                        className="w-100 p-2 m-0"
                                        min={this.telemetryInfo.firstTimeStamp.valueOf()}
                                        max={this.telemetryInfo.lastTimeStamp.valueOf()}
                                        defaultValue={[
                                            this.formStore.fields.startTime.value.valueOf(),
                                            this.formStore.fields.endTime.value.valueOf(),
                                        ]}
                                        tooltipPlacement={this.appStore.hiddenHeader ? 'bottom' : 'top'}
                                        steps={20 * 60 * 1000}
                                        formatTooltip={this.formatTooltip}
                                        onChange={this.onDateChange}
                                    />
                                )}
                        </div>
                    </div>
                </SubTopBar>

                <div className="row h-100">
                    <div className="col-12 h-100">
                        {!this.loading && !!this.chartData && (
                            <div className="bg-gray-darker rounded h-100 live-chart-container d-flex flex-column">
                                {this.appStore.hiddenHeader && (
                                    <a
                                        className="btn btn-link expand-window-button ms-auto"
                                        onClick={this.appStore.showHeader}
                                    >
                                        <BsArrowsAngleContract className="text-white" />
                                    </a>
                                )}
                                {!this.appStore.hiddenHeader && (
                                    <a
                                        className="btn btn-link expand-window-button ms-auto"
                                        onClick={this.appStore.hideHeader}
                                    >
                                        <BsArrowsAngleExpand className="text-white" />
                                    </a>
                                )}
                                <LineGraph
                                    data={this.chartData}
                                    displayLabels={!this.appStore.isMobile}
                                    yAxes={this.yAxes}
                                    hideDots
                                    hideXLines
                                    onLegendClick={this.legendClick}
                                    onClick={this.zoomOnData}
                                    legendPosition="top"
                                    fontColor="#fff"
                                    gridColor="#343a40"
                                    yMax={this.singleYMax}
                                    yMin={this.singleYMin}
                                    ignoreAspectRatio
                                    timeScale
                                    enablePan
                                    enableZoom
                                />
                            </div>
                        )}

                        {(this.loading || !this.chartData) && (
                            <div className="bg-gray-darker rounded h-100 live-chart-container d-flex flex-column">
                                <div className="flex-items-center h-100 w-100">
                                    <Loader loading={this.loading} message="Loading Data" showSpinner center />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    public onSensorFilterChange(value: { option: Mri.WebApi.MeasurementId }[] | null | undefined) {
        this.displaysChartsStore.storage.telemetryChartSelectedMeasurements = value?.map(c => c.option) ?? [];
    }

    @bind
    @action
    public onFilterDropdownToggle(isOpen: boolean) {
        this.filterOpen = isOpen;
    }

    @bind
    @action
    public onForcePercentagesChange() {
        this.forcePercentages = !this.forcePercentages;
    }

    @bind
    @action
    public onForceSingleYAxis() {
        this.forceSingleYAxis = !this.forceSingleYAxis;
    }

    @bind
    @action
    public setSensorsFromList(e: any, measurements: Mri.WebApi.MeasurementId[]) {
        e.preventDefault();

        this.displaysChartsStore.storage.telemetryChartSelectedMeasurements = measurements;
    }

    @bind
    @action
    private onDateChange(v: number[]) {
        const start = new Date(v[0]);
        const end = new Date(v[1]);
        this.formStore.fields.startTime.value = start;
        this.formStore.fields.endTime.value = end;
    }

    @bind
    private formatTooltip(v: number) {
        return displayFormatTime(new Date(v), 'M/d/yy h:mm a', this.props.match.params.serialNumber);
    }

    @bind
    @action
    private async loadTrackData() {
        if (
            !this.formStore.fields.startTime.value ||
            !this.formStore.fields.endTime.value ||
            this.loading ||
            !this.displaysChartsStore.storage.telemetryChartSelectedMeasurements
        )
            return;

        this.loading = true;
        try {
            const promises = this.displaysChartsStore.storage.telemetryChartSelectedMeasurements.map(s =>
                this.displaysChartsStore.loadTelemetryMeasurementsAsync(
                    this.props.match.params.serialNumber,
                    s,
                    this.formStore.fields.startTime.value!,
                    this.formStore.fields.endTime.value!
                )
            );
            await Promise.all(promises);
        } catch (e: any) {
            console.error(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private onFilterClear() {
        this.displaysChartsStore.storage.telemetryChartSelectedMeasurements = [];
    }

    //private scaleData(data: IChartPoint[]): [{ min: number; max: number }, IChartPoint[]] {
    //    const range = {
    //        min: 0,
    //        max: 100,
    //    };

    //    const scaledData = data.map(d => ({ x: d.x, y: d.y * 100 }));

    //    return [range, scaledData];
    //}

    @bind
    @action
    private zoomOnData(activeElements: IActiveElement[], event: any) {
        if (activeElements.length) {
            const clickedDatasetIndex = activeElements[0].datasetIndex;
            const clickedElementindex = activeElements[0].index;

            if (this.chartData && this.chartData.datasets) {
                const filtered = this.chartData.datasets[clickedDatasetIndex];
                this.hiddenLabels = this.chartData.datasets.filter(d => d.label != filtered.label).map(d => d.label!);
                filtered.hidden = true;
            }
        } else if (!this.legendClicked) {
            this.hiddenLabels = [];
        }

        this.legendClicked = false;
    }

    @bind
    @action
    private legendClick(event: Chart.ChartEvent, legendItem: Chart.LegendItem) {
        this.legendClicked = true;

        const legend = legendItem;

        if (legend.hidden) {
            this.hiddenLabels.splice(this.hiddenLabels.indexOf(legend.text!), 1);
        } else {
            this.hiddenLabels.push(legend.text!);
        }
    }

    @computed
    private get chartData(): Chart.ChartData<'line'> | undefined {
        if (this.formStore.fields.startTime.value == undefined) return;
        if (this.formStore.fields.endTime.value == undefined) return;
        if (this.measurementDatas == undefined) return;

        const utcToZonedTime = displayUtcToZonedTimeFactory(this.props.match.params.serialNumber);
        const datasets: Chart.ChartDataset<'line'>[] =
            this.displaysChartsStore.storage.telemetryChartSelectedMeasurements
                .map((s, idx) => {
                    const measurementData = this.measurementDatas!.get(s) ?? [];
                    let data: IChartPoint[] = measurementData
                        .filter(
                            d =>
                                d.timeStamp >= this.formStore.fields.startTime.value! &&
                                d.timeStamp <= this.formStore.fields.endTime.value!
                        )
                        .map(d => ({ x: utcToZonedTime(d.timeStamp).valueOf(), y: d.value }));

                    const [min, max] = minMax(data.map(d => d.y));
                    const range = this.expectedRanges.reduce((p, c) =>
                        Math.abs(c.max - max) < Math.abs(p.max - max) ? c : p
                    );
                    const percentage = min >= 0 && max <= 1;
                    const usePercentages = this.forcePercentages && percentage;

                    // if (min >= 0 && range.min == 0 && range.max == 1) [range, data] = this.scaleData(data);

                    data = data.map(d => {
                        let yValue = usePercentages ? Number((d.y * 100).toFixed(2)) : Number(d.y.toFixed(2));
                        if (yValue < this.singleYMin) this.singleYMin = yValue;
                        if (yValue > this.singleYMax) this.singleYMax = yValue;

                           return ({
                                x: d.x,
                                y: yValue,
                            })
                    });

                    const dataSet: Chart.ChartDataset = {
                        type: 'line',
                        label: Enums.toString(Enums.MeasurementId, s),
                        borderColor: this.borderColors[idx % this.borderColors.length],
                        backgroundColor: this.borderColors[idx % this.borderColors.length],
                        borderWidth: 0.8,
                        tension: 0.15,
                        fill: false,
                        hidden: this.hiddenLabels.includes(Enums.toString(Enums.MeasurementId, s)),
                        yAxisID: `axis-${range.min}-${range.max}`,
                        data,
                    };

                    return dataSet;
                })
                .filter(ds => ds.data?.length);

        const firstDataSet: Chart.Point[] = (datasets.find(ds => ds.data?.length)?.data as Chart.Point[]) ?? [];
        const labels: any = firstDataSet.map(d => d.x);

        return {
            labels,
            datasets,
        };
    }

    @computed
    private get telemetryInfo() {
        return this.displaysChartsStore.displaysTelemetryInfos.get(this.props.match.params.serialNumber);
    }

    @computed
    private get measurementDatas() {
        return this.displaysChartsStore.displayMeasurementDatas.get(this.props.match.params.serialNumber);
    }

    @computed
    private get timezone() {
        return this.displaysStore.timezones.get(this.props.match.params.serialNumber);
    }

    @computed
    private get yAxes(): { [key: string]: Chart.ScaleOptionsByType<keyof Chart.CartesianScaleTypeRegistry> } {
        if (!this.chartData?.datasets) return {} as any;
        const yAxes: { [key: string]: Chart.ScaleOptionsByType<keyof Chart.CartesianScaleTypeRegistry> } = {};

        if (!this.forceSingleYAxis) {
            for (const d of this.chartData?.datasets) {
                const newChartId = d.yAxisID;

                if (newChartId) {
                    if (!!yAxes[newChartId]) continue;
                    const match = /axis-(-?[0-9]+)-(-?[0-9]+)/.exec(newChartId);

                    yAxes[newChartId] = {
                        axis: 'y',
                        type: 'linear',
                        position: 'left',
                        suggestedMin: parseInt(match![1]),
                        suggestedMax: parseInt(match![2]),
                    } as any;
                }
            }
        }

        return yAxes;
    }

    @computed
    private get options() {
        return (
            this.telemetryInfo?.availableMeasurements.map(m => {
                return { option: m };
            }) ??
            toKeyArray(Enums.MeasurementId).map(m => {
                return { option: m };
            })
        );
    }
   
    @computed
    private get display() {
        return this.displaysStore.displays.get(this.props.match.params.serialNumber);
    }

    private print(entity: string | Mri.WebApi.MeasurementId) {
        return typeof entity == 'number' ? Enums.toString(Enums.MeasurementId, entity) : entity;
    }

    /**
     * Sets the initial time range for the telemetry chart.
     * If the telemetry chart has data, the initial time range will be the last 7 days of data.
     * If the telemetry chart does not have data, the initial time range will be the last 7 days from today.
     * If start and end time are set to save in the store (default behavior), the saved start and end time will check
     * to make sure they are within the range of available data. If they are not, the start and end time will be set
     * to the first and/or last time stamp of the available data.
     * @param hasData
     * @private
     */

    private formStore = new FormStore({
        startTime: new Date(),
        endTime: new Date(),
    });

    @observable
    private loading = false;
    @observable
    private endTime?: Date;
    @observable
    private hiddenLabels: string[] = [];
    @observable
    private legendClicked: boolean = false;

    private expectedRanges = [
        {
            min: 0,
            max: 1,
        },
        {
            min: 0,
            max: 10,
        },
        {
            min: -40,
            max: 90,
        },
        {
            min: 0,
            max: 100,
        },
        {
            min: 0,
            max: 240,
        },
        {
            min: 0,
            max: 5000,
        },
        {
            min: 0,
            max: 20000,
        },
    ];

    private borderColors = [
        '#00FF00',
        '#FFFF00',
        '#00FFFF',
        '#FF00FF',
        '#099FFF',
        '#FF0000',
        '#CC00FF',
        '#A6CEE3',
        '#1F78B4',
        '#B2DF8A',
        '#33A02C',
        '#FB9A99',
        '#E31A1C',
        '#FDBF6F',
        '#FF7F00',
        '#CAB2D6',
        '#6A3D9A',
        '#FFFF99',
        '#B15928',
        '#8DD3C7',
        '#FFFFB3',
        '#BEBADA',
        '#FB8072',
        '#80B1D3',
        '#FDB462',
        '#B3DE69',
        '#FCCDE5',
        '#D9D9D9',
        '#BC80BD',
        '#CCEBC5',
        '#FFED6F',
    ];
    @observable
    private filterOpen = false;
    @observable
    private forcePercentages = false;
    @observable
    private forceSingleYAxis = false;
    @observable
    private startTimeInitialized = false;
    @observable
    private singleYMin = 0;
    @observable
    private singleYMax = 100;
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private displaysChartsStore!: DisplaysChartsStore;
}
